/**
 * Propagarea DEK-urilor de atașamente către share-urile adăugate ulterior.
 *
 * Fiecare atașament are DEK-ul lui, împachetat la upload doar pentru userii
 * care aveau share în acel moment. Un destinatar nou primește DEK-ul notiței,
 * dar nu și pe cele ale atașamentelor — aici le completăm: desfacem DEK-ul cu
 * wrap-ul nostru și îl împachetăm pentru cheia publică a fiecărui user rămas
 * pe dinafară. Serverul nu suprascrie wrap-uri existente, deci e idempotent.
 */
import { assetsApi } from '@/api/assets';
import { tenantsApi } from '@/api/tenants';
import { base64ToBytes, bytesToBase64, unwrapKey, wrapKey, type KeyPair } from '@/crypto/sodium';

/** Întoarce numărul de atașamente completate. */
export async function propagateAssetWraps(args: {
  noteId: string;
  tenantId: string;
  keyPair: KeyPair;
}): Promise<number> {
  const { noteId, tenantId, keyPair } = args;
  const states = await assetsApi.listWraps(noteId);
  const fixable = states.filter((s) => s.dek_wrapped && s.missing_user_ids.length > 0);
  if (fixable.length === 0) return 0;

  const members = await tenantsApi.listMembers(tenantId);
  const publicKeys = new Map(members.map((m) => [m.user_id, m.public_key]));

  const wraps: Record<string, Record<string, string>> = {};
  for (const state of fixable) {
    const dek = await unwrapKey(base64ToBytes(state.dek_wrapped!), keyPair);
    try {
      const perUser: Record<string, string> = {};
      for (const userId of state.missing_user_ids) {
        const publicKey = publicKeys.get(userId);
        if (!publicKey) continue; // share rămas de la un fost membru
        perUser[userId] = bytesToBase64(await wrapKey(dek, base64ToBytes(publicKey)));
      }
      if (Object.keys(perUser).length > 0) wraps[state.asset_id] = perUser;
    } finally {
      dek.fill(0);
    }
  }
  if (Object.keys(wraps).length === 0) return 0;
  return (await assetsApi.addWraps(noteId, wraps)).added;
}
